import React, { useContext } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useFormik from 'hooks/useFormik';
import FormInput from 'components/Form/Input';
import FormInputRow from 'components/Form/InputRow';
import ActionRow from 'components/ActionRow';
import Loader from 'components/Loaders/Loader';
import authService from 'store/auth/service';
import { Snackbar } from 'components/Snackbars';
import {
  DashboardPage,
  ResetPasswordByEmailPage,
  LoginPage,
} from 'routes/paths';
import { ContentBox } from 'components/ContentBox';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { CompanyLogo } from 'components/CompanyLogo';
import { CompanyContext } from 'index';
import useSelector from 'hooks/useSelector';
import { rolesSelector, tokenSelector } from 'store/user/selectors';
import { UserRoleEnum } from 'common/user/enums/user-role.enum';

import { validationSchema } from './validationSchema';
import styles from './styles.module.scss';
import { IUser } from 'store/user/interfaces';
import { isProduction } from '../../../config/environment/environment.helper';
import { useQueryParams } from '../../../hooks/useSearchParams';

export const LoginByEmailPage = () => {
  const queryParams = useQueryParams();

  const userOnline = useSelector(tokenSelector);
  const userRoles = useSelector(rolesSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const company = useContext(CompanyContext);

  const [login, { isLoading }] = authService.useLoginMutation();
  const [checkUser] = authService.useLazyCheckUserQuery();

  const isValidAuthorizedManager =
    !!userOnline && !!userRoles && userRoles.includes(UserRoleEnum.MANAGER);

  const { getFieldProps, getFieldErrorProp, handleSubmit } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (value) => {
      try {
        const userData = await login({
          email: value.email,
          password: value.password,
        });
        if ((userData as any).error) {
          return (
            <Snackbar
              message={(userData as any).error.massage || 'Invalid Credentials'}
            />
          );
        }
        await checkUser();
        const user = (userData as any).data as IUser;
        window.analytics.identify(user._id, {
          created: new Date(),
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          roles: user.roles,
          hostname: window.location.hostname,
        });
        return navigate(state?.path || DashboardPage);
      } catch (error: any) {
        return <Snackbar message={error.message} />;
      }
    },
  });

  if (isValidAuthorizedManager) {
    return <Navigate to={DashboardPage} replace />;
  }

  if (isProduction()) {
    if (!queryParams?.code || queryParams.code !== 'ja8723ghjg81h346j') {
      return <Navigate to={LoginPage} replace />;
    }
  }

  return (
    <div className={styles.root}>
      <ContentBox className={styles.content}>
        <CompanyLogo src={company.logo} responsive={true} />
        <form onSubmit={handleSubmit} className={styles.form}>
          <FormInputRow
            className={styles.inputRow}
            required
            {...getFieldErrorProp('email')}
          >
            <FormInput
              {...getFieldProps('email')}
              placeholder={t('LoginPage.label.email') || ''}
            />
          </FormInputRow>
          <FormInputRow
            className={styles.inputRow}
            required
            {...getFieldErrorProp('password')}
          >
            <FormInput
              {...getFieldProps('password')}
              type="password"
              autoComplete="password"
              placeholder={t('LoginPage.label.password') || ''}
            />
          </FormInputRow>
          <ActionRow style="space-between">
            <Link to={ResetPasswordByEmailPage}>
              {t('LoginPage.btn.reset-password')}
            </Link>
            <ButtonWithArrowIcon type="submit" disabled={isLoading}>
              {t('LoginPage.btn.submitV1')}
            </ButtonWithArrowIcon>
          </ActionRow>
          {isLoading && <Loader />}
        </form>
      </ContentBox>
    </div>
  );
};
